import { useState, useEffect } from 'react';
import { profileService, Profile } from '@/services/profileService';
import { userService, UserProfile } from '@/services/userService';
import { useAuth } from '@/context/AuthContext';

interface UseUserProfileReturn {
    user: UserProfile | null;
    profile: Profile | null;
    isLoading: boolean;
    error: string | null;
    refreshProfile: () => Promise<void>;
}

const STORAGE_KEY = 'trenio_user_profile_cache';
const EVENT_KEY = 'trenio_user_profile_updated';

// Helper to add cache-busting timestamp to avatar URLs (only if not already present)
function addCacheBustingToUrl(url: string | undefined | null): string | undefined {
    if (!url) return undefined;
    // Only add timestamp if URL doesn't already have query params (to avoid duplicates)
    if (url.includes('?')) return url;
    return `${url}?t=${Date.now()}`;
}

export function useUserProfile(): UseUserProfileReturn {
    const { user: authUser } = useAuth();

    // Initialize from localStorage for instant render
    const [user, setUser] = useState<UserProfile | null>(() => {
        try {
            const cached = localStorage.getItem(STORAGE_KEY);
            return cached ? JSON.parse(cached).user : null;
        } catch {
            return null;
        }
    });

    const [profile, setProfile] = useState<Profile | null>(() => {
        try {
            const cached = localStorage.getItem(STORAGE_KEY);
            return cached ? JSON.parse(cached).profile : null;
        } catch {
            return null;
        }
    });

    // If we have cached data, we're not "loading" in the UI sense,
    // but we might be revalidating in the background.
    const [isLoading, setIsLoading] = useState(!user);
    const [error, setError] = useState<string | null>(null);

    const loadProfile = async (background = false) => {
        if (!background && !user) setIsLoading(true);
        setError(null);
        try {
            // Load user data from auth
            const userData = await userService.getProfile();

            // Load profile data
            const profileData = await profileService.getProfile();

            if (userData && profileData?.profile) {
                const updatedUser = {
                    ...userData,
                    role: userData.role, // Explicitly include role
                    profilePhotoUrl: addCacheBustingToUrl(userData.profilePhotoUrl || profileData.profile.profilePhotoUrl),
                    firstName: userData.firstName,
                    lastName: userData.lastName,
                    phone: profileData.profile.phone,
                };

                // Update state
                setUser(updatedUser);
                setProfile(profileData.profile);

                // Update cache
                localStorage.setItem(STORAGE_KEY, JSON.stringify({
                    user: updatedUser,
                    profile: profileData.profile
                }));
            }
        } catch (err) {
            console.error('Error loading profile:', err);
            if (!user) setError('Failed to load profile');
        } finally {
            setIsLoading(false);
        }
    };

    // Reset state when auth user changes (login/logout/switch account)
    useEffect(() => {
        if (!authUser) {
            // User logged out - clear state
            setUser(null);
            setProfile(null);
            setIsLoading(false);
            return;
        }

        // User logged in (or changed) - check if cached data belongs to this user
        const cached = localStorage.getItem(STORAGE_KEY);
        if (cached) {
            try {
                const data = JSON.parse(cached);
                if (data.user?.id === authUser.id) {
                    // Cache belongs to this user - use it and revalidate in background
                    setUser(data.user);
                    setProfile(data.profile);
                    loadProfile(true);
                    return;
                }
            } catch { }
        }

        // No valid cache for this user - clear stale state and do a fresh load
        setUser(null);
        setProfile(null);
        setIsLoading(true);
        loadProfile(false);
    }, [authUser?.id]);

    useEffect(() => {
        // Listen for updates from other components (like SettingsPage)
        const handleUpdate = () => {
            // Optimistic update from cache first if available
            try {
                const cached = localStorage.getItem(STORAGE_KEY);
                if (cached) {
                    const data = JSON.parse(cached);
                    // Add cache busting to avatar URL when reading from cache
                    if (data.user?.profilePhotoUrl) {
                        data.user.profilePhotoUrl = addCacheBustingToUrl(data.user.profilePhotoUrl);
                    }
                    setUser(data.user);
                    setProfile(data.profile);
                }
            } catch (e) {
                console.error('Error reading cache event:', e);
            }
            // Then revalidate
            loadProfile(true);
        };

        window.addEventListener(EVENT_KEY, handleUpdate);
        return () => window.removeEventListener(EVENT_KEY, handleUpdate);
    }, []);

    return {
        // Guard against stale data: if authUser changed but useEffect hasn't run yet,
        // don't return profile data from a different user
        user: (user && authUser && user.id !== authUser.id) ? null : user,
        profile: (user && authUser && user.id !== authUser.id) ? null : profile,
        isLoading: (authUser && (!user || user.id !== authUser.id)) ? true : isLoading,
        error,
        refreshProfile: () => loadProfile(false),
    };
}
